import {
  type BackgroundProps,
  Badge,
  Box,
  Divider,
  HStack,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { t } from "@lingui/macro";
import type { OurWorkBudgetItemStats } from "@src/__generated__/urql-graphql";
import type { ProjectModel } from "@src/components/modules/projects/detail/models";
import { ProgressBarOurWorkSpend } from "@src/components/ui-kit";
import { DashedDivider } from "@src/components/ui-kit/DashedDivider";
import { Icon } from "@src/components/ui-kit/Icon";
import { currency, percent } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import type React from "react";
import { Fragment, type FunctionComponent } from "react";

// const HUNDRED_PERCENT = 10000

type TStats = Pick<
  OurWorkBudgetItemStats,
  | "spent_budget_percent"
  | "spent_budget"
  | "total_budget"
  | "total_time"
  | "credit"
  | "spent_time"
  | "remaining_time"
>;

interface OurWorkProps {
  project: ProjectModel;
}

export const OurWorkTooltipLabelRow: FunctionComponent<{
  title: string;
  time?: number;
  budget?: number;
  percentNumeric?: number;
  bg?: BackgroundProps["bg"];
  hideBadge?: boolean;
}> = ({ title, time, budget, percentNumeric, bg, hideBadge }) => {
  const store = useStore();

  const readableTime =
    store.workspaceStore.settings?.timeTrackingRateValueReadable(time ?? 0);
  const formattedBudget = currency.formatByWorkspace(budget ?? 0);
  const formattedPercent = percent.format(percentNumeric ?? 0);

  return (
    <VStack alignItems="flex-start" px="5" spacing="1">
      <Text fontWeight="medium">{title}</Text>
      <HStack spacing="2">
        <HStack color={bg}>
          {title === "Tracked" && <Icon name="clock" />}
          <Text color={bg} fontWeight="semibold">
            {readableTime}
          </Text>
        </HStack>
        <Divider h="4" orientation="vertical" />
        <Text color={bg} fontWeight="semibold">
          {formattedBudget}
        </Text>
        {!hideBadge && (
          <Badge px="2" bg={bg}>
            {formattedPercent}
          </Badge>
        )}
      </HStack>
    </VStack>
  );
};

export const OurWorkTooltipLabel: FunctionComponent<{
  stats: TStats;
  hideBadges?: boolean;
  isTimeAndMaterials?: boolean;
  isTemplate: boolean;
}> = ({ stats, hideBadges, isTimeAndMaterials, isTemplate }) => (
  <Stack alignItems="flex-start" py="5" spacing="3">
    {!isTimeAndMaterials && (
      <OurWorkTooltipLabelRow
        title={t`Budget`}
        time={stats.total_time}
        budget={stats.total_budget}
        hideBadge={true}
      />
    )}
    {!isTimeAndMaterials && !isTemplate && <DashedDivider />}
    {!isTemplate && (
      <OurWorkTooltipLabelRow
        title={t`Tracked`}
        time={stats.spent_time}
        budget={stats.spent_budget}
        percentNumeric={stats.spent_budget_percent}
        bg="teal.300"
        hideBadge={hideBadges}
      />
    )}
    {/* TODO: Finish after API will be done */}
    {/* <TooltipLabelRow
        title={t`Allocated`}
        time={0}
        budget={0}
        percentNumeric={0}
        bg="plum"
        hideBadge={hideBadges}
      />
      <TooltipLabelRow
        title={t`Left to allocate`}
        time={0}
        budget={0}
        percentNumeric={0}
        bg="gray.200"
      />
      <TooltipLabelRow
        title={t`Over budget`}
        time={0}
        budget={0}
        percentNumeric={0}
        bg="red.500"
        hideBadge={hideBadges}
      /> */}
  </Stack>
);

const OurWork: FunctionComponent<React.PropsWithChildren<OurWorkProps>> = ({
  project,
  children,
}) => {
  const stats = project.stats.ourWork;

  return (
    <Fragment>
      <ProgressBarOurWorkSpend
        total_budget={stats.total_budget}
        spent_budget={stats.spent_budget}
        credit={stats.credit}
        variant="big"
        textOnHover={`${currency.formatByWorkspace(stats.spent_budget)} (${percent.format(
          stats.spent_budget_percent,
        )})`}
        isTimeAndMaterials={project.isTimeAndMaterials}
      />

      {!project.isTimeAndMaterials ? (
        <Box>
          <Text pt="2" fontSize="smaller">{t`Work progress:`}</Text>
          {children}
        </Box>
      ) : (
        <Box />
      )}
    </Fragment>
  );
};

export default observer(OurWork);
